import React from 'react';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-4">
        <Link
          to="/dashboard"
          className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Dashboard
        </Link>
      </div>
      <div className="bg-white rounded-lg shadow-md p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Terms of Service</h1>

        <p className="text-sm text-gray-500 mb-8">
          Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </p>

        <div className="space-y-6 text-gray-700">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Agreement to Terms</h2>
            <p>
              By accessing or using BoardRez, you agree to be bound by these Terms of Service and our Privacy Policy.
              If you do not agree to these terms, please do not use our service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Description of Service</h2>
            <p className="mb-3">
              BoardRez provides a platform for climbing gyms to manage climbing board reservations and for climbers
              to reserve training time on those boards. Our service includes:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Online reservation system for climbing boards</li>
              <li>Gym management tools for administrators</li>
              <li>Email notifications for reservations and reminders</li>
              <li>QR code check-in functionality</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">User Accounts</h2>
            <p className="mb-3">You are responsible for:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Providing accurate and current information</li>
              <li>Notifying us immediately of any unauthorized use of your account</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Acceptable Use</h2>
            <p className="mb-3">You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the service for any unlawful purpose</li>
              <li>Make reservations you do not intend to use</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the service or servers</li>
              <li>Use automated systems to make bulk reservations</li>
              <li>Share your account credentials with others</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Reservations</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Reservations are subject to availability and gym policies</li>
              <li>Gyms may set their own cancellation policies</li>
              <li>No-shows may result in restrictions on future reservations</li>
              <li>We reserve the right to cancel reservations that violate these terms</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Payment Terms</h2>
            <p>
              BoardRez is a platform provider. Any fees for reservations or memberships are determined by
              individual climbing gyms. Payment processing and refund policies are subject to each gym's terms.
              Platform subscription fees for gym owners are billed according to the selected plan and are
              non-refundable except as required by law.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Liability and Disclaimers</h2>
            <p className="mb-3">
              BoardRez provides the reservation platform "as is" without warranties of any kind. We are not
              responsible for:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>The operation or safety of individual climbing gyms</li>
              <li>Injuries or accidents that occur at climbing facilities</li>
              <li>Disputes between users and gyms</li>
              <li>Interruptions or errors in the service</li>
              <li>Loss of data or reservations due to technical issues</li>
            </ul>
            <p className="mt-3 font-medium">
              Climbing is inherently dangerous. Users assume all risks associated with climbing activities.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Intellectual Property</h2>
            <p>
              All content on BoardRez, including text, graphics, logos, and software, is the property of
              BoardRez, LLC or its licensors and is protected by copyright and other intellectual property laws.
              You may not reproduce, distribute, or create derivative works without our express written permission.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Termination</h2>
            <p>
              We reserve the right to suspend or terminate your account at any time for violations of these
              terms or for any other reason at our discretion. You may close your account at any time by
              contacting us. Upon termination, your right to use the service immediately ceases.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Changes to Terms</h2>
            <p>
              We may update these Terms of Service from time to time. We will notify users of material changes
              by email or through the platform. Continued use of the service after changes constitutes acceptance
              of the new terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Governing Law</h2>
            <p>
              These terms are governed by the laws of the United States and the state in which BoardRez, LLC
              is registered, without regard to conflict of law provisions. Any disputes shall be resolved
              through binding arbitration or in the courts of competent jurisdiction.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Contact Information</h2>
            <p>
              If you have any questions about these Terms of Service, please contact us at{' '}
              <a href="mailto:support@boardrez.com" className="text-teal-600 hover:text-teal-700 font-medium">
                support@boardrez.com
              </a>
              .
            </p>
          </section>

          <section className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Questions?</h2>
            <p>
              We're here to help! If you have any questions about these terms or how our service works,
              please don't hesitate to{' '}
              <Link to="/contact" className="text-teal-600 hover:text-teal-700 font-medium">
                contact us
              </Link>
              .
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
